import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Countdown } from "../components/Countdown";
import { ParallaxBg } from "../components/ParallaxBg";

const EVENT = {
  title: "The VA Home-Ready Blueprint — Live Webinar",
  details: "Free 60-minute live session for VA buyers with Matthew Lake-Nestor (Nexa Lending). Access link will be sent by email before the event.",
  startUTC: "20260917T230000Z",
  endUTC: "20260918T000000Z",
};

const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(EVENT.title)}&dates=${EVENT.startUTC}/${EVENT.endUTC}&details=${encodeURIComponent(EVENT.details)}&location=${encodeURIComponent("Online — link sent by email")}`;

const downloadIcs = () => {
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Nexa Lending//VA Home-Ready Blueprint//EN",
    "BEGIN:VEVENT",
    `UID:va-blueprint-${Date.now()}@nexalending`,
    `DTSTAMP:${EVENT.startUTC}`,
    `DTSTART:${EVENT.startUTC}`,
    `DTEND:${EVENT.endUTC}`,
    `SUMMARY:${EVENT.title}`,
    `DESCRIPTION:${EVENT.details}`,
    "LOCATION:Online — link sent by email",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "va-home-ready-blueprint.ics";
  a.click();
  URL.revokeObjectURL(url);
};

const GUIDES = [
  { file: "/guides/va-entitlement-funding-fee-guide.pdf", icon: "fa-solid fa-file-pdf", title: "VA Entitlement & Funding Fee Breakdown Guide", desc: "What your entitlement covers, what the funding fee costs, and exemptions — in plain English.", testid: "download-entitlement-guide" },
  { file: "/guides/closing-cost-concessions-checklist.pdf", icon: "fa-solid fa-list-check", title: "Closing-Cost & Concessions Checklist", desc: "Every line item to account for, and what sellers can realistically cover.", testid: "download-closing-checklist" },
];

export default function ThankYou() {
  const { state } = useLocation();
  const firstName = state?.firstName;
  useEffect(() => {
    document.title = "You're Registered — The VA Home-Ready Blueprint";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="wam-page min-h-screen relative overflow-hidden" data-testid="thank-you-page">
      <ParallaxBg image="/bg/home5.jpg" opacity={0.12} />
      <div className="grain-overlay" />
      <div className="relative max-w-[860px] mx-auto px-6 pt-24 pb-28">
        <div className="text-center mb-14">
          <div className="mx-auto mb-7 w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "rgba(61,123,255,0.12)", border: "1.5px solid #3D7BFF" }}>
            <i className="fa-solid fa-check text-3xl" style={{ color: "#3D7BFF" }}></i>
          </div>
          <p className="eyebrow mb-4">SEAT RESERVED — SEPTEMBER 17TH · 7:00 PM EST</p>
          <h1 className="font-black uppercase mb-5" style={{ fontSize: "clamp(32px, 5vw, 56px)", letterSpacing: "-0.03em", lineHeight: 0.98 }} data-testid="thank-you-heading">
            {firstName ? `${firstName}, you're in.` : "You're in."}
          </h1>
          <p style={{ color: "rgba(240,236,228,0.55)", fontSize: 15, lineHeight: 1.7, maxWidth: 520 }} className="mx-auto">
            A confirmation email with your event access link is on its way. Here's everything you need before the session.
          </p>
        </div>

        <div className="glass-card p-7 md:p-9 mb-6" data-testid="calendar-card">
          <p className="eyebrow mb-3">STEP 1 — BLOCK THE TIME</p>
          <h2 className="font-extrabold text-lg mb-6">Add the webinar to your calendar</h2>
          <div className="flex justify-center mb-8"><Countdown /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <a href={googleCalUrl} target="_blank" rel="noreferrer" className="btn-accent w-full" data-testid="add-google-calendar-button">
              <i className="fa-brands fa-google"></i> GOOGLE CALENDAR
            </a>
            <button className="btn-outline w-full justify-center" data-testid="add-ics-calendar-button" onClick={downloadIcs}>
              <i className="fa-regular fa-calendar-plus"></i> APPLE / OUTLOOK (.ICS)
            </button>
          </div>
        </div>

        <div className="glass-card p-7 md:p-9 mb-6" data-testid="downloads-card">
          <p className="eyebrow mb-3">STEP 2 — GRAB YOUR GUIDES</p>
          <h2 className="font-extrabold text-lg mb-6">Your free bonus downloads</h2>
          <div className="space-y-3">
            {GUIDES.map((g) => (
              <a key={g.testid} href={g.file} download className="guide-row" data-testid={g.testid}>
                <i className={g.icon}></i>
                <span className="flex-1">
                  <span className="block font-bold text-sm">{g.title}</span>
                  <span className="block text-xs mt-1" style={{ color: "var(--muted)" }}>{g.desc}</span>
                </span>
                <span className="mono" style={{ fontSize: 10, letterSpacing: "0.16em", color: "#3D7BFF" }}>DOWNLOAD ↓</span>
              </a>
            ))}
          </div>
          <p className="mt-5" style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", lineHeight: 1.7 }}>
            Your Same-Week Pre-Approval Scheduling Priority and optional Free VA Buyer Pre-Approval Strategy Call details will arrive by email from Matthew personally.
          </p>
        </div>

        <div className="text-center pt-6">
          <Link to="/" className="mono" style={{ fontSize: 11, letterSpacing: "0.18em", color: "rgba(255,255,255,0.45)" }} data-testid="back-home-link">
            ← BACK TO THE EVENT PAGE
          </Link>
        </div>
      </div>
    </div>
  );
}
